import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta } from '../../web/ReactQueryProvider';

interface IDailyActivityRole {
	role?: string;
}

/**
 * Hook for checking if the current user has manager permissions (admin OR manager role)
 * This matches the backend logic from RepCoachingGoalsService.CanReadOrModifyAsync
 */
export function useManagerPermissions() {
	const userSession = useUserSession();
	const isAdmin = userSession.account.isAdmin;

	const { data: currentDayActivity, isLoading } = useQuery({
		queryKey: ['currentDayActivity', userSession.user.id],
		// The caller's own day, which is what the server checks. The `summary` endpoint is admin-only, so a non-admin
		// reading it never sees their own MGR role.
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IDailyActivityRole>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'userDailyActivity' }),
				'GET'
			),
		enabled: !!userSession.user.id && !isAdmin,
		staleTime: 5 * 60 * 1000,
		// Callers wait on isLoading before falling back to non-manager UI, so fail fast rather than retry.
		retry: false,
		meta: {
			errorLogging: { action: 'useManagerPermissions', category: 'ManagerPermissions' },
		} satisfies LevStandardQueryMeta,
	});

	const isManager = currentDayActivity?.role === 'MGR';

	return {
		hasManagerPermissions: isAdmin || isManager,
		isLoading: !isAdmin && isLoading,
		isAdmin,
		isManager,
	};
}
